<template>
  <div>
    <section-header title="" style="margin-bottom: 30px">
      <c-button
        v-if="showActionButtons"
        key="add-btn"
        size="sm"
        icon="fa-regular fa-plus"
        theme="success"
        @click="openCreateModal"
      >Add {{ elementName }}</c-button>

      <c-button-help
        v-if="helpButtonUrl"
        key="help-btn"
        :link="helpButtonUrl"
      />
    </section-header>

    <section-card
      :loading="isFetching"
      :loading-lines="5"
      loading-pattern="rows"
      :icon="pageTitleIcon"
      :title="infoTitle"
      :desc="infoDescription"
      :color="cardColor"
      :admin-only="adminOnly"
    >
      <section-empty
        v-show="!items.length"
        message="No items created yet"
      />

      <draggable
        v-model="localItems"
        tag="ul"
        class="list-unstyled"
        :id="`${listName}-list`"
        handle=".icl-drag-handle"
        :animation="150"
        ghost-class="icl-drag-ghost"
        chosen-class="icl-drag-chosen"
        :disabled="!orderUrl || isSorting"
        @end="onSortEnd"
      >
        <li
          v-for="item in localItems"
          :id="`${listName}-${item.id}`"
          :key="`${listName}-${item.id}`"
        >
          <c-sortable-item
            :hideEditButton="!showActionButtons"
            :hideRemoveButton="!showActionButtons"
            @edit="openEditModal(item)"
            @remove="openRemoveConfirmModal(item)"
          >
            <i v-if="orderUrl" class="icl-drag-handle fa-regular fa-grip-dots-vertical"></i>
            <i :class="`fa-duotone fa-lg fa-fw ${listIcon}`"></i>
            {{ item[listItemName] }}
            <span v-if="listItemAdditionalField">({{ item[listItemAdditionalField] }})</span>
          </c-sortable-item>
        </li>
      </draggable>

    </section-card>

    <c-confirm-delete-modal
      :is-open="showRemoveConfirmModal"
      @close="showRemoveConfirmModal = false"
      @confirm="remove"
      :show-indicator="showIndicator"
      :title="`Delete ${elementName}`"
      :description="`Are you sure you want to delete this ${elementName}?:`"
      :body="currentItem[listItemName]"
    />

    <c-modal
      v-if="showCreateEditModal"
      :size="modalSize"
      :title="`${currentItem.id ? 'Edit' : 'Add a New'} ${elementName}`"
      @close="showCreateEditModal = false"
    >
      <validation-observer v-slot="{ invalid, valid, errors, ...rest }" ref="form">
        <form @submit.prevent="submit">
          <div class="modal-body">
            <c-select
              v-if="templates && templates.length"
              :empty-option="true"
              empty-option-label="- Select -"
              label="Select Template"
              v-model="templateId"
              :options="templates"
            />

            <fragment v-for="(item, index) in fields" :key="`field-${index}`">
              <validation-provider :vid="item.name" :rules="item.isRequired && 'required'" v-slot="{ errors }">
                <c-input
                  v-if="item.type === 'text'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                />

                <c-textarea
                  v-if="item.type === 'textarea'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  :rows="item.rows"
                  v-model="currentItem[item.name]"
                />

                <c-editor
                  :id="`editor-${index}`"
                  v-if="item.type === 'editor'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                />

                <c-checkbox
                  v-if="item.type === 'checkbox'"
                  :label="item.label"
                  :error="errors[0]"
                  v-model="currentItem[item.name]"
                />
              </validation-provider>
            </fragment>
          </div>
          <div class="modal-footer">
            <div>
              <c-button type="button" theme="default" icon="fa-remove" @click="closeCreateEditModal()">
                Close
              </c-button>

              <div class="right-buttons-wrapper">
                <c-checkbox
                  class="mr-2"
                  v-if="showSaveAsTemplate"
                  v-model="currentItem.as_template"
                  label="Save as Template"
                />

                <c-button type="submit" theme="success" icon="fa-save" :show-indicator="showIndicator">Save</c-button>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </c-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { fetchData, putData } from "../../api/network";

export default {
  components: { draggable },
  props: {
    modalSize: {
      type: String,
      default: "md",
    },
    infoTitle: {
      type: String,
      default: "",
    },
    pageTitleIcon: {
      type: String,
      default: "fa-circle-info",
    },
    infoDescription: {
      type: String,
      default: "",
    },
    cardColor: {
      type: String,
      default: "",
    },
    adminOnly: {
      type: Boolean,
      default: false,
    },
    listIcon: {
      type: String,
      default: "fa-circle-dot text-primary",
    },
    showActionButtons: {
      type: Boolean,
      default: true,
    },
    showSaveAsTemplate: {
      type: Boolean,
      default: false,
    },
    listName: {
      type: String,
      default: "item",
    },
    listItemName: {
      type: String,
      default: "name",
    },
    elementName: {
      type: String,
      default: "item",
    },
    helpButtonUrl: {
      type: String,
      default: "",
    },
    templateDataByIdUrl: {
      type: String,
      default: "",
    },
    isFetching: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    templates: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      default: () => [],
    },
    showIndicator: {
      type: Boolean,
      default: false,
    },
    listItemAdditionalField: {
      type: String,
      default: "",
    },
    orderUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      showCreateEditModal: false,
      showRemoveConfirmModal: false,
      currentItem: {},
      templateId: null,
      localItems: [],
      isSorting: false,
    };
  },
  watch: {
    items: {
      immediate: true,
      handler(val) {
        // Reconcile incoming items over current local order instead of overwriting it.
        // Drag-reorder lives only in localItems (persisted to the server via onSortEnd),
        // so a parent reassigning `items` on edit/create/remove must not reset that order.
        const incoming = val || [];
        const incomingById = new Map(incoming.map((item) => [item.id, item]));

        // Keep current drag order for items that still exist, with refreshed content.
        const ordered = this.localItems
          .filter((item) => incomingById.has(item.id))
          .map((item) => incomingById.get(item.id));

        // Append newly created items (not yet in local order), preserving incoming order.
        const existingIds = new Set(ordered.map((item) => item.id));
        const appended = incoming.filter((item) => !existingIds.has(item.id));

        this.localItems = [...ordered, ...appended];
      },
    },
    templateId: {
      handler(newVal) {
        const baseUrl = `${this.templateDataByIdUrl}/${newVal}`;

        if (newVal) {
          fetchData(baseUrl)
            .then((resp) => {
              const id = this.currentItem.id ? this.currentItem.id : null;

              const data = { ...resp.data.fields };
              delete data.id;
              this.currentItem = data;

              this.fields.forEach((field, index) => {
                if (field.type === "editor") {
                  $(`#editor-${index}`).summernote("code", resp.data.fields[`${field.name}`]);
                }
              });

              if (id) {
                this.currentItem.id = id;
              }
            })
            .catch(() => {});
        }
      },
      deep: false,
    },
  },
  methods: {
    resetCurrent() {
      this.currentItem = {};
      this.templateId = null;
    },

    openCreateModal() {
      this.resetCurrent();
      this.showCreateEditModal = true;
    },

    openEditModal(item) {
      this.currentItem = { ...item };
      this.showCreateEditModal = true;
    },

    closeCreateEditModal() {
      this.showCreateEditModal = false;
      this.resetCurrent();
    },

    submit() {
      this.$refs.form.validate().then((success) => {
        if (!success) {
          return;
        }

        if (this.currentItem.id) {
          this.$emit("edit", this.currentItem, this.$refs.form, this.closeCreateEditModal);
        } else {
          this.$emit("create", this.currentItem, this.$refs.form, this.closeCreateEditModal);
        }

        this.$nextTick(() => {
          this.$refs.form.reset();
        });
      });
    },

    openRemoveConfirmModal(item) {
      this.currentItem = { ...item };
      this.showRemoveConfirmModal = true;
    },

    closeRemoveConfirmModal() {
      this.showRemoveConfirmModal = false;
      this.resetCurrent();
    },

    remove() {
      this.$emit("remove", this.currentItem, this.closeRemoveConfirmModal);
    },

    onSortEnd() {
      if (!this.orderUrl) return;

      this.isSorting = true;
      putData(this.orderUrl, { [this.listName]: this.localItems.map((item) => item.id) })
        .finally(() => {
          this.isSorting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.icl-drag-handle {
  cursor: grab;
  color: #bbb;
  margin-right: 6px;

  &:hover { color: #888; }
  &:active { cursor: grabbing; }
}

::v-deep .icl-drag-ghost { opacity: 0.35; }
::v-deep .icl-drag-chosen { background: #f8faff; box-shadow: 0 4px 14px rgba(0,0,0,0.08); }

.modal-footer {
  display: flex;
  align-items: center;

  & > div {
    display: flex;
    justify-content: space-between;
    width: 100%;

    & .right-buttons-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      & .app-checkbox {
        margin-right: 5px;
      }
    }
  }
}
</style>
